import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  type DragEndEvent,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Link } from 'react-router';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { GripVertical, MoreHorizontal, Pencil, Plus } from 'lucide-react';
import type { UserRole } from '@/features/courses/useCourse';
import { Tooltip } from '@/components/Tooltip/Tooltip';
import {
  getQuestionEditTargetId,
  useOptionalActiveEdit,
  useOptionalPageEditMode,
} from '@/features/content/editor/ContentEditModeProvider';
import type {
  EditableQuestion,
  EditableQuestionPart,
  QuestionSectionKind,
} from '@/features/content/editor/editorTypes';
import { buildPromptPreviewUnits } from '@/features/content/editor/promptContentUnits';
import {
  buildRepresentativeSubstitute,
  identitySubstitute,
  type SubstituteFn,
} from '@/features/content/editor/representativeVariablePreview';
import { QuestionSelectionToolbar } from '@/features/content/questionActions/QuestionSelectionToolbar';
import { useQuestionSelection } from '@/features/content/questionActions/useQuestionSelection';

import {
  buildQuestionSubmissionContext,
  getSectionClosedMessage,
  QuestionCard,
} from './QuestionCard';
import {
  getPartLabel,
  type NormalizedQuestionData,
  type NormalizedQuestionSectionData,
} from './questionNormalizer';
import { getDisplayedQuestions } from './displayedQuestions';
import { useChapterReread } from './useQuestionSubmission';
import styles from './QuestionSection.module.css';

// The chapter is read again at least this long after its close, so that the
// server's clock has passed the due date too: the first read after it is the
// one that closes the chapter.
const CLOSE_REREAD_DELAY_MS = 1000;
// ...and up to this much later again, at random. Every student's page open at
// the due time read the chapter in the same second, and each read that finds
// the chapter not yet complete runs the server's close of it, whose writes
// share one entity group (review of PR 99). The card refuses a check or a save
// from the close itself, so the wait lets nothing through.
const CLOSE_REREAD_SPREAD_MS = 60_000;
// The longest delay setTimeout holds (about 24.8 days).
const MAX_TIMER_DELAY_MS = 2 ** 31 - 1;

interface QuestionSectionProps {
  areSolutionsVisible: boolean;
  anchorId?: string;
  className?: string;
  componentId: string;
  courseId: string;
  courseSubject?: string | null;
  chapterTitle: string;
  dueStatusText?: string;
  hideStats?: boolean;
  isSectionClosed: boolean;
  isSectionOpen: boolean;
  isPageMultiSelectActive?: boolean;
  section: NormalizedQuestionSectionData;
  questionSections?: NormalizedQuestionSectionData[];
  structuralEditsRestricted?: boolean;
  transitionNotice?: string;
  onCopyQuestions?: (
    scope: QuestionSectionKind,
    questions: EditableQuestion[],
  ) => void;
  onBeforeBulkAction?: () => boolean;
  onBeforeEdit?: () => void;
  onMultiSelectActiveChange?: (scope: string, active: boolean) => void;
  userRole: UserRole;
}

function getScoreSummary(section: NormalizedQuestionSectionData) {
  // The total score lives only in the review summary banner; the section
  // header just reports how many questions it contains.
  return `${section.questionCount} question${section.questionCount === 1 ? '' : 's'}`;
}

function getQuestionGroupActionLabel(kind: QuestionSectionKind) {
  return kind === 'preLecture' ? 'Add Pre-Lecture Question' : 'Add Homework Question';
}

function createOptionContentUnits(questionId: string, option: string, index: number) {
  const contentHtml = option.trim() ? option : `Option ${index + 1}`;

  return [
    {
      collapseByDefault: false,
      disableAutoNumbering: false,
      html: contentHtml,
      id: `${questionId}-option-${index + 1}`,
      kind: 'text' as const,
      summaryLabel: `Option ${index + 1}`,
    },
  ];
}

/**
 * The Edit button of the question with this edit target id. It is not drawn
 * while its question is being edited, so the editor gives focus back to the
 * one drawn in its place when it closes (#173).
 */
export function findQuestionEditButton(targetId: string): HTMLElement | null {
  const shell = [...document.querySelectorAll<HTMLElement>('[data-question-target-id]')].find(
    (element) => element.dataset.questionTargetId === targetId,
  );

  return shell?.querySelector<HTMLElement>('[data-question-edit-button]') ?? null;
}

export function normalizeEditableQuestionForDisplay(
  question: EditableQuestion | EditableQuestionPart,
  context: {
    fallback?: NormalizedQuestionData;
    index: number;
    isInstructorView?: boolean;
    isNestedPart?: boolean;
    kind: QuestionSectionKind;
    parentQuestionId?: string;
    questionSectionId: string;
    // Substitute representative values for ##VAR## so the edit-mode list preview
    // renders numbers (not a KaTeX parse error). Computed once on the parent and
    // threaded to nested parts (which share the parent's variables).
    representativeValues?: boolean;
    sectionId: string;
    substitute?: SubstituteFn;
  },
): NormalizedQuestionData {
  const fallback = context.fallback;
  const questionId = question.id;
  const substitute = context.substitute ?? identitySubstitute;
  const pointsPossible =
    question.value ?? fallback?.pointsPossible ?? (question.type === 'AI_UPLOAD' ? 0 : 1);
  const base = {
    allowAIHints: fallback?.allowAIHints,
    attemptsLabel: fallback?.attemptsLabel ?? 'Attempt 0 of 0',
    attemptsUsed: fallback?.attemptsUsed ?? 0,
    canCheck: fallback?.canCheck ?? false,
    canSave: fallback?.canSave ?? false,
    feedback: fallback?.feedback,
    hasAggregateStats: fallback?.hasAggregateStats,
    hintSetting: fallback?.hintSetting,
    hint: fallback?.hint,
    id: questionId,
    isAssigned: fallback?.isAssigned ?? !context.isNestedPart,
    isRemoved: fallback?.isRemoved,
    isInstructorView: context.isInstructorView ?? fallback?.isInstructorView,
    isInstructorStatsMode: fallback?.isInstructorStatsMode,
    isLocked: fallback?.isLocked ?? false,
    isParticipationOnly:
      ('creditForParticipation' in question
        ? question.creditForParticipation
        : undefined) ??
      fallback?.isParticipationOnly ??
      false,
    isPastDue: fallback?.isPastDue,
    isReviewMode: fallback?.isReviewMode,
    // A part with no saved counterpart (one added since the page was saved)
    // was numbered here, beside parts the student view letters (issue #92).
    label:
      fallback?.label ??
      (context.isNestedPart
        ? getPartLabel(context.index)
        : context.kind === 'preLecture'
          ? `Pre-Lecture Question ${context.index + 1}`
          : `Question ${context.index + 1}`),
    maxAttempts: fallback?.maxAttempts,
    parentQuestionId: context.parentQuestionId ?? fallback?.parentQuestionId,
    partLabel: fallback?.partLabel,
    pointsPossible,
    // Edit-mode preview: the prompt text PLUS the question's image content units,
    // so images stay visible inline (not just in the editor dialog). createPromptUnits
    // built only a text unit, which is why images vanished when Edit Content was on.
    promptUnits: buildPromptPreviewUnits(
      substitute(question.contentHtml),
      question.promptImages ?? [],
    ),
    questionSectionId: context.questionSectionId,
    scoreEarned: fallback?.scoreEarned,
    sectionId: context.sectionId,
    solution: fallback?.solution,
    solutionsAvailableAtUtcMilli: fallback?.solutionsAvailableAtUtcMilli,
    solutionsVisible: fallback?.solutionsVisible,
    uid: 'uid' in question && question.uid ? question.uid : questionId,
    usesRandomVariables:
      ('useRandomVariables' in question ? question.useRandomVariables : undefined) ??
      fallback?.usesRandomVariables,
    usesRepresentativeVariableValues: context.representativeValues ?? false,
  };

  if (question.type === 'AI_UPLOAD') {
    const fallbackAI = fallback?.kind === 'aiUpload' ? fallback : undefined;
    const gradingRubric = question.gradingRubric
      ? {
          rubricUnits: question.gradingRubric.rubricUnits.map((unit, index) => ({
            details: unit.details,
            id: unit.id ?? `${questionId}-rubric-${index + 1}`,
            name: unit.name,
            score: unit.score,
          })),
          totalScore: question.gradingRubric.totalScore,
        }
      : fallbackAI?.gradingRubric ?? {
          rubricUnits: [],
          totalScore: pointsPossible,
        };

    return {
      ...base,
      gradingDetails: fallbackAI?.gradingDetails,
      gradingRubric,
      kind: 'aiUpload',
      submittedFileName: fallbackAI?.submittedFileName,
      submittedFileUrl: fallbackAI?.submittedFileUrl,
    };
  }

  if (question.type === 'EXACT_ANSWER') {
    const fallbackExact = fallback?.kind === 'exactAnswer' ? fallback : undefined;

    return {
      ...base,
      correctAnswer: question.exactAnswer ?? fallbackExact?.correctAnswer,
      currentRandomExactAnswer: fallbackExact?.currentRandomExactAnswer,
      inputValue: fallbackExact?.inputValue ?? '',
      kind: 'exactAnswer',
      units: question.answerUnits ?? fallbackExact?.units,
    };
  }

  if (question.type === 'MULTI_PART') {
    const fallbackMultiPart = fallback?.kind === 'multiPart' ? fallback : undefined;
    const parts = (question.parts ?? []).map((part, index) =>
      normalizeEditableQuestionForDisplay(part, {
        fallback: fallbackMultiPart?.parts[index],
        index,
        isInstructorView: context.isInstructorView,
        isNestedPart: true,
        kind: context.kind,
        parentQuestionId: questionId,
        questionSectionId: context.questionSectionId,
        // Parts share the parent's variables: substitute their content too, but
        // the representative note is shown once on the parent, not per part.
        sectionId: context.sectionId,
        substitute: context.substitute,
      }),
    );

    return {
      ...base,
      kind: 'multiPart',
      parts,
    };
  }

  if (question.type === 'NUMERICAL_WITH_ERROR') {
    const fallbackNumerical = fallback?.kind === 'numerical' ? fallback : undefined;

    return {
      ...base,
      correctAnswer: question.numericalAnswer ?? fallbackNumerical?.correctAnswer,
      correctAnswerExtraExponent: fallbackNumerical?.correctAnswerExtraExponent,
      currentRandomNumericalAnswer:
        fallbackNumerical?.currentRandomNumericalAnswer,
      currentRandomNumericalAnswerExtraExponent:
        fallbackNumerical?.currentRandomNumericalAnswerExtraExponent,
      errorTolerance: question.numericalError ?? fallbackNumerical?.errorTolerance,
      inputValue: fallbackNumerical?.inputValue ?? '',
      kind: 'numerical',
      units: question.answerUnits ?? fallbackNumerical?.units,
    };
  }

  // Select-all fell through to multiple choice below, so edit mode drew it as
  // one-choice radios with one right answer marked (issue #92).
  if (question.type === 'SELECT_ALL') {
    const fallbackSelectAll = fallback?.kind === 'selectAll' ? fallback : undefined;
    const optionValues = question.options.length > 0
      ? question.options
      : fallbackSelectAll?.options.map((option) => option.text ?? option.label) ??
        ['', ''];

    return {
      ...base,
      answerIndexStats: fallbackSelectAll?.answerIndexStats,
      correctAnswerIndices:
        question.correctAnswerIndices ?? fallbackSelectAll?.correctAnswerIndices ?? [],
      kind: 'selectAll',
      options: optionValues.map((option, index) => ({
        contentUnits: createOptionContentUnits(questionId, substitute(option), index),
        id: `${questionId}-option-${index + 1}`,
        index,
        label: String.fromCharCode(65 + index),
        text: option,
      })),
      selectedIndices: fallbackSelectAll?.selectedIndices ?? [],
      units: question.answerUnits ?? fallbackSelectAll?.units,
    };
  }

  const fallbackMultipleChoice =
    fallback?.kind === 'multipleChoice' ? fallback : undefined;
  const fallbackOptions =
    fallbackMultipleChoice?.options.map((option) => option.text ?? option.label) ??
    ['', ''];
  const optionValues = question.options.length > 0
    ? question.options
    : fallbackOptions;

  return {
    ...base,
    answerIndexStats: fallbackMultipleChoice?.answerIndexStats,
    correctAnswerIndex:
      question.correctAnswerIndex ?? fallbackMultipleChoice?.correctAnswerIndex,
    currentRandomExactAnswer: fallbackMultipleChoice?.currentRandomExactAnswer,
    currentRandomNumericalAnswer:
      fallbackMultipleChoice?.currentRandomNumericalAnswer,
    currentRandomNumericalAnswerExtraExponent:
      fallbackMultipleChoice?.currentRandomNumericalAnswerExtraExponent,
    kind: 'multipleChoice',
    options: optionValues.map((option, index) => ({
      contentUnits: createOptionContentUnits(questionId, substitute(option), index),
      id: `${questionId}-option-${index + 1}`,
      index,
      label: String.fromCharCode(65 + index),
      text: option,
    })),
    selectedIndex: fallbackMultipleChoice?.selectedIndex,
    units: question.answerUnits ?? fallbackMultipleChoice?.units,
  };
}

function createEditModeQuestionList(
  section: NormalizedQuestionSectionData,
  editableQuestions: EditableQuestion[],
) {
  const fallbackById = new Map<string, NormalizedQuestionData>();

  section.questions.forEach((question) => {
    fallbackById.set(question.id, question);
    fallbackById.set(question.uid, question);
  });

  return editableQuestions.map((question, index) => {
    const { hasRepresentativeValues, substitute } =
      buildRepresentativeSubstitute(question);

    return normalizeEditableQuestionForDisplay(question, {
      fallback:
        fallbackById.get(question.id) ??
        (question.uid ? fallbackById.get(question.uid) : undefined),
      index,
      isInstructorView: section.isInstructorView,
      kind: section.kind,
      questionSectionId: section.questionSectionId,
      representativeValues: hasRepresentativeValues,
      sectionId: section.id,
      substitute,
    });
  });
}

export function QuestionSection({
  areSolutionsVisible,
  anchorId,
  chapterTitle,
  className,
  componentId,
  courseId,
  courseSubject,
  dueStatusText,
  hideStats = false,
  isPageMultiSelectActive = false,
  isSectionClosed,
  isSectionOpen,
  section,
  questionSections,
  structuralEditsRestricted = false,
  transitionNotice,
  onBeforeBulkAction,
  onCopyQuestions,
  onBeforeEdit,
  onMultiSelectActiveChange,
  userRole,
}: QuestionSectionProps) {
  const activeEdit = useOptionalActiveEdit();
  const pageEditMode = useOptionalPageEditMode();
  const questionSelection = useQuestionSelection();
  const {
    cancelSelection,
    enterSelection,
    isSelectionActiveForScope,
    selectedCount,
    selectedQuestionIdsSet,
    toggleQuestion,
  } = questionSelection;
  const [announcement, setAnnouncement] = useState(
    'Question section is ready.',
  );
  const rereadChapter = useChapterReread(courseId, componentId);
  const closesAtUtcMilli = section.closesAtUtcMilli;
  const sectionRef = useRef<HTMLElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const wasOpenRef = useRef(isSectionOpen);
  const hadFocusRef = useRef(false);
  // Whether the chapter has been read, or is about to be, since the section's
  // close: by the timer below, by a submission the card refused, or by the
  // closing itself (further down). One read per close is enough.
  const hasReadAtCloseRef = useRef(false);
  const closeReadTimerRef = useRef<number | undefined>(undefined);
  const readAtClose = useCallback(() => {
    if (hasReadAtCloseRef.current) {
      return;
    }

    hasReadAtCloseRef.current = true;
    // Never sooner than a second after the close, so that the server's clock
    // has passed it too: a read before then grades nothing.
    const wait =
      closesAtUtcMilli === undefined
        ? 0
        : closesAtUtcMilli + CLOSE_REREAD_DELAY_MS - Date.now();

    if (wait <= 0) {
      void rereadChapter();
      return;
    }

    closeReadTimerRef.current = window.setTimeout(() => {
      void rereadChapter();
    }, wait);
  }, [closesAtUtcMilli, rereadChapter]);

  useEffect(() => () => window.clearTimeout(closeReadTimerRef.current), []);

  // The server has refused an answer as past its close (issue #44). Its clock is the
  // one that counts, so this read is now, whatever this page's clock says. It is apart
  // from the read above, which a close passed offline spends for nothing: it fails, or
  // comes from the worker's cache. One read, however many answers are refused: a
  // refusal changes nothing on the server, and each read can run the close, which
  // grades the class.
  //
  // While this page's clock has not reached the close, the read above is still to come.
  // This one can be too soon to run the close, which the server starts only in the second
  // after the due date, and without the other the closed view would show a saved answer
  // ungraded until a reload (review of PR 163).
  const hasReadAfterRefusalRef = useRef(false);
  const readAfterRefusal = useCallback(() => {
    if (hasReadAfterRefusalRef.current) {
      return;
    }

    hasReadAfterRefusalRef.current = true;

    if (closesAtUtcMilli === undefined || Date.now() >= closesAtUtcMilli) {
      hasReadAtCloseRef.current = true;
    }

    void rereadChapter();
  }, [closesAtUtcMilli, rereadChapter]);

  // Nothing redraws a page when its section's due date passes, so a page left
  // open kept offering Check after the close (issue #92, POST-02). Read the
  // chapter again just after the close: the page then draws its closed view.
  // Students only: staff submit nothing, and an instructor may be editing.
  useEffect(() => {
    if (
      userRole !== 'student' ||
      !isSectionOpen ||
      closesAtUtcMilli === undefined
    ) {
      return undefined;
    }

    const delay =
      closesAtUtcMilli -
      Date.now() +
      CLOSE_REREAD_DELAY_MS +
      Math.floor(Math.random() * CLOSE_REREAD_SPREAD_MS);

    // Past the close already, the card refuses instead. Beyond what a timer
    // can hold, the page will have been read again long before.
    if (delay <= 0 || delay > MAX_TIMER_DELAY_MS) {
      return undefined;
    }

    const timer = window.setTimeout(readAtClose, delay);

    return () => window.clearTimeout(timer);
  }, [closesAtUtcMilli, isSectionOpen, readAtClose, userRole]);

  // Whether the last focus on the page was in this section. Focus on a button
  // the close takes away falls to nowhere, without a focus event saying so.
  useEffect(() => {
    if (userRole !== 'student' || !isSectionOpen) {
      return undefined;
    }

    function recordFocus(event: FocusEvent) {
      hadFocusRef.current =
        event.target instanceof Node &&
        Boolean(sectionRef.current?.contains(event.target));
    }

    document.addEventListener('focusin', recordFocus);

    return () => document.removeEventListener('focusin', recordFocus);
  }, [isSectionOpen, userRole]);

  // A page that closed itself changed with nothing said, and a focused Save or
  // Check went with it (review of PR 99). Say that the section closed and,
  // when the close took the focus away, put it on the section's heading.
  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = isSectionOpen;

    if (isSectionOpen) {
      // Open (again, after a time extension): its next close reads again.
      hasReadAtCloseRef.current = false;
      hasReadAfterRefusalRef.current = false;
      window.clearTimeout(closeReadTimerRef.current);
      return;
    }

    if (userRole !== 'student' || !wasOpen || !isSectionClosed) {
      return;
    }

    // The page also turns closed on its own clock, whenever anything redraws
    // it after the due date, and that clears the timer above before it fires.
    // The record it drew then is from before the server's close, which grades
    // what was saved: the closed view showed 0 for those answers until a
    // reload (review of PR 99). So the closing reads the chapter too, unless a
    // read since the close is already under way.
    readAtClose();

    const message = getSectionClosedMessage(closesAtUtcMilli, section.kind);
    // A check refused at the close has said it already, and started the read.
    setAnnouncement((current) => (current.startsWith(message) ? current : message));

    // Focus that went with the close is on nothing: the page itself, or an
    // element no longer on it.
    const focused = document.activeElement;
    const isFocusLost =
      focused === null || focused === document.body || !focused.isConnected;

    if (hadFocusRef.current && isFocusLost) {
      headingRef.current?.focus({ preventScroll: true });
    }
  }, [
    closesAtUtcMilli,
    isSectionClosed,
    isSectionOpen,
    readAtClose,
    section.kind,
    userRole,
  ]);
  const [activeDraggedQuestionId, setActiveDraggedQuestionId] = useState<
    string | null
  >(null);
  const [activeDraggedQuestionWidth, setActiveDraggedQuestionWidth] = useState<
    number | null
  >(null);
  const canEditQuestions =
    userRole === 'instructor' && Boolean(pageEditMode?.editMode);
  const isQuestionSelectionActive =
    isSelectionActiveForScope(section.kind);
  const restrictedSectionLabel =
    section.kind === 'preLecture' ? 'Pre-lecture' : 'Homework';
  const restrictionMessage = `${restrictedSectionLabel} is visible to students. Some edit functionality is disabled.`;
  const restrictedDeleteReason = `Cannot delete while ${restrictedSectionLabel.toLowerCase()} is visible to students.`;
  const restrictedDuplicateReason = `Cannot duplicate while ${restrictedSectionLabel.toLowerCase()} is visible to students.`;
  const restrictedReorderReason = `Cannot reorder while ${restrictedSectionLabel.toLowerCase()} is visible to students.`;
  const addQuestionLabel = getQuestionGroupActionLabel(section.kind);
  const editableQuestions = section.kind === 'preLecture'
    ? activeEdit?.editor.preLectureQuestions ?? []
    : activeEdit?.editor.questions ?? [];
  const editableQuestionByLookupId = useMemo(() => {
    const lookup = new Map<string, EditableQuestion>();

    editableQuestions.forEach((question) => {
      lookup.set(question.id, question);

      if (question.uid) {
        lookup.set(question.uid, question);
      }
    });

    return lookup;
  }, [editableQuestions]);
  const displayQuestions = useMemo(
    () =>
      canEditQuestions
        ? createEditModeQuestionList(section, editableQuestions)
        : section.questions,
    [canEditQuestions, editableQuestions, section],
  );
  const displaySection = useMemo(
    () =>
      canEditQuestions
        ? {
            ...section,
            possiblePoints: displayQuestions
              .filter((question) => question.isAssigned)
              .reduce((total, question) => total + question.pointsPossible, 0),
            questionCount: displayQuestions.length,
            questions: displayQuestions,
          }
        : section,
    [canEditQuestions, displayQuestions, section],
  );
  // A chapter with no questions, a reading say, is not homework until it has
  // one: no homework heading, count or due line, and its banner says only what
  // the lock means for it (issue #174). A chapter with pre-lecture questions
  // keeps its homework section as it is: theirs says homework is below.
  const isEmptyInEditMode =
    canEditQuestions &&
    displaySection.questionCount === 0 &&
    !questionSections?.some((other) => other.kind !== section.kind);
  const questionIndexByTargetId = useMemo(
    () =>
      new Map(
        editableQuestions.map((question, index) => [
          getQuestionEditTargetId(section.kind, question.id),
          index,
        ]),
      ),
    [editableQuestions, section.kind],
  );
  const sortableSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const homeworkSubmissionSection = useMemo(
    () =>
      section.kind === 'homework'
        ? displaySection
        : questionSections?.find(
            (candidate) =>
              candidate.id === section.id && candidate.kind === 'homework',
          ) ?? null,
    [displaySection, questionSections, section.id, section.kind],
  );
  const preLectureSubmissionSection = useMemo(
    () =>
      section.kind === 'preLecture'
        ? displaySection
        : questionSections?.find(
            (candidate) =>
              candidate.id === section.id && candidate.kind === 'preLecture',
          ) ?? null,
    [displaySection, questionSections, section.id, section.kind],
  );
  const submissionContext = useMemo(
    () =>
      buildQuestionSubmissionContext(displaySection, {
        homeworkSection: homeworkSubmissionSection,
        preLectureSection: preLectureSubmissionSection,
      }),
    [displaySection, homeworkSubmissionSection, preLectureSubmissionSection],
  );
  const questionAiContext = useMemo(
    () => ({
      chapterTitle,
      courseSubject,
      sectionKind: section.kind,
    }),
    [chapterTitle, courseSubject, section.kind],
  );
  // A question removed from the assignment stays in displayQuestions (and so in
  // the submission context) at its position, and getDisplayedQuestions leaves it
  // out of what is rendered.
  const removedQuestionCount = displayQuestions.filter((question) => question.isRemoved).length;
  const { assigned: assignedQuestions, practice: practiceQuestions } = useMemo(
    () => getDisplayedQuestions(displayQuestions, section.kind),
    [displayQuestions, section.kind],
  );

  useEffect(() => {
    onMultiSelectActiveChange?.(
      `questions:${section.kind}`,
      isQuestionSelectionActive,
    );
  }, [
    isQuestionSelectionActive,
    onMultiSelectActiveChange,
    section.kind,
  ]);

  const getEditableQuestion = useCallback(
    (question: NormalizedQuestionData) =>
      editableQuestionByLookupId.get(question.uid) ??
      editableQuestionByLookupId.get(question.id),
    [editableQuestionByLookupId],
  );

  const getEditableQuestionId = useCallback((question: NormalizedQuestionData) => {
    const matchingQuestion = getEditableQuestion(question);

    return matchingQuestion?.id ?? question.uid ?? question.id;
  }, [getEditableQuestion]);

  const getSelectedEditableQuestions = useCallback(() => {
    return editableQuestions.filter((question) =>
      selectedQuestionIdsSet.has(question.id),
    );
  }, [editableQuestions, selectedQuestionIdsSet]);

  const canRunBulkAction = useCallback(() => {
    return onBeforeBulkAction?.() ?? true;
  }, [onBeforeBulkAction]);
  const activeEditRef = useRef(activeEdit);
  const canRunBulkActionRef = useRef(canRunBulkAction);
  const cancelSelectionRef = useRef(cancelSelection);
  const editableQuestionByLookupIdRef = useRef(editableQuestionByLookupId);
  const enterSelectionRef = useRef(enterSelection);
  const isPageMultiSelectActiveRef = useRef(isPageMultiSelectActive);
  const onBeforeEditRef = useRef(onBeforeEdit);
  const onCopyQuestionsRef = useRef(onCopyQuestions);
  const structuralEditsRestrictedRef = useRef(structuralEditsRestricted);
  const toggleQuestionRef = useRef(toggleQuestion);

  useEffect(() => {
    activeEditRef.current = activeEdit;
    canRunBulkActionRef.current = canRunBulkAction;
    cancelSelectionRef.current = cancelSelection;
    editableQuestionByLookupIdRef.current = editableQuestionByLookupId;
    enterSelectionRef.current = enterSelection;
    isPageMultiSelectActiveRef.current = isPageMultiSelectActive;
    onBeforeEditRef.current = onBeforeEdit;
    onCopyQuestionsRef.current = onCopyQuestions;
    structuralEditsRestrictedRef.current = structuralEditsRestricted;
    toggleQuestionRef.current = toggleQuestion;
  }, [
    activeEdit,
    canRunBulkAction,
    cancelSelection,
    editableQuestionByLookupId,
    enterSelection,
    isPageMultiSelectActive,
    onBeforeEdit,
    onCopyQuestions,
    structuralEditsRestricted,
    toggleQuestion,
  ]);

  const activeDraggedQuestion = useMemo(() => {
    if (!activeDraggedQuestionId) {
      return null;
    }

    return (
      displayQuestions.find(
        (question) =>
          getQuestionEditTargetId(
            section.kind,
            getEditableQuestionId(question),
          ) === activeDraggedQuestionId,
      ) ?? null
    );
  }, [
    activeDraggedQuestionId,
    displayQuestions,
    getEditableQuestionId,
    section.kind,
  ]);

  const handleQuestionCopy = useCallback(
    (kind: QuestionSectionKind, questionId: string) => {
      if (!canRunBulkActionRef.current()) {
        return;
      }

      const editableQuestion =
        editableQuestionByLookupIdRef.current.get(questionId);

      if (editableQuestion) {
        onCopyQuestionsRef.current?.(kind, [editableQuestion]);
      }
    },
    [],
  );

  const handleQuestionDelete = useCallback(
    (kind: QuestionSectionKind, questionId: string) => {
      const currentActiveEdit = activeEditRef.current;

      if (
        !currentActiveEdit ||
        structuralEditsRestrictedRef.current ||
        !canRunBulkActionRef.current()
      ) {
        return;
      }

      if (
        window.confirm(
          'Delete this item? You can undo by canceling page edits before saving.',
        )
      ) {
        currentActiveEdit.deleteQuestion(kind, questionId);
      }
    },
    [],
  );

  const handleQuestionDuplicate = useCallback(
    (kind: QuestionSectionKind, questionId: string) => {
      const currentActiveEdit = activeEditRef.current;

      if (
        !currentActiveEdit ||
        structuralEditsRestrictedRef.current ||
        !canRunBulkActionRef.current()
      ) {
        return;
      }

      currentActiveEdit.duplicateQuestion(kind, questionId);
    },
    [],
  );

  const handleQuestionEdit = useCallback(
    (kind: QuestionSectionKind, questionId: string) => {
      const currentActiveEdit = activeEditRef.current;

      if (!currentActiveEdit) {
        return;
      }

      cancelSelectionRef.current();
      onBeforeEditRef.current?.();
      currentActiveEdit.openQuestionEdit(kind, questionId);
    },
    [],
  );

  const handleQuestionSelectMultiple = useCallback(
    (kind: QuestionSectionKind, questionId: string) => {
      if (
        isPageMultiSelectActiveRef.current ||
        !canRunBulkActionRef.current()
      ) {
        return;
      }

      enterSelectionRef.current(kind, questionId);
    },
    [],
  );

  const handleQuestionToggleSelected = useCallback(
    (kind: QuestionSectionKind, questionId: string) => {
      toggleQuestionRef.current(kind, questionId);
    },
    [],
  );

  function getInsertIndexBefore(question: NormalizedQuestionData | undefined) {
    if (!question) {
      return editableQuestions.length;
    }

    const targetId = getQuestionEditTargetId(
      section.kind,
      getEditableQuestionId(question),
    );
    const sourceIndex = questionIndexByTargetId.get(targetId);

    return sourceIndex ?? 0;
  }

  function handleQuestionDragEnd(event: DragEndEvent) {
    setActiveDraggedQuestionId(null);
    setActiveDraggedQuestionWidth(null);

    const { active, over } = event;

    if (
      !activeEdit ||
      !over ||
      active.id === over.id ||
      activeEdit.isAnyUnitActive ||
      structuralEditsRestricted
    ) {
      return;
    }

    const fromIndex = questionIndexByTargetId.get(String(active.id)) ?? -1;
    const toIndex = questionIndexByTargetId.get(String(over.id)) ?? -1;

    if (fromIndex < 0 || toIndex < 0) {
      return;
    }

    activeEdit.moveQuestion(section.kind, fromIndex, toIndex);
  }

  function handleQuestionDragCancel() {
    setActiveDraggedQuestionId(null);
    setActiveDraggedQuestionWidth(null);
  }

  function renderQuestionInsertDivider(index: number, key: string) {
    if (!canEditQuestions || structuralEditsRestricted || !activeEdit) {
      return null;
    }

    return (
      <QuestionInsertDivider
        key={key}
        onInsert={() => {
          activeEdit.insertQuestionAt(section.kind, index);
        }}
      />
    );
  }

  function handleAppendQuestion() {
    if (!activeEdit || structuralEditsRestricted) {
      return;
    }

    if (isQuestionSelectionActive || isPageMultiSelectActive) {
      return;
    }

    onBeforeEdit?.();
    activeEdit.insertQuestionAt(section.kind, editableQuestions.length);
  }

  function renderAppendQuestionButton() {
    if (!canEditQuestions || structuralEditsRestricted || !activeEdit) {
      return null;
    }

    const isDisabled = isQuestionSelectionActive || isPageMultiSelectActive;
    const buttonText =
      section.kind === 'preLecture' ? 'Add Pre-Lecture Question' : 'Add Question';

    return (
      <button
        aria-label={addQuestionLabel}
        className={styles.addQuestionButton}
        disabled={isDisabled}
        onClick={handleAppendQuestion}
        type="button"
      >
        <Plus aria-hidden="true" size={16} strokeWidth={2.4} />
        <span>{buttonText}</span>
      </button>
    );
  }

  function renderQuestionItem({
    headingLevel,
    isAssigned = true,
    question,
  }: {
    headingLevel?: 4;
    isAssigned?: boolean;
    question: NormalizedQuestionData;
  }) {
    const editableQuestion = getEditableQuestion(question);
    const editableQuestionId = editableQuestion?.id ?? getEditableQuestionId(question);
    const targetId = getQuestionEditTargetId(section.kind, editableQuestionId);
    const isSelectionActive = isQuestionSelectionActive;
    const questionCard = (
      <QuestionCard
        areSolutionsVisible={areSolutionsVisible}
        closesAtUtcMilli={section.closesAtUtcMilli}
        componentId={componentId}
        courseId={courseId}
        aiContext={questionAiContext}
        headingLevel={headingLevel}
        hideStats={hideStats}
        isAssigned={isAssigned}
        isSectionClosed={isSectionClosed}
        isSectionOpen={isSectionOpen}
        onAnnounce={isAssigned ? setAnnouncement : undefined}
        onClosed={readAtClose}
        onRefusedAfterClose={readAfterRefusal}
        question={question}
        role={userRole}
        submissionContext={submissionContext}
      />
    );

    if (!activeEdit || !canEditQuestions) {
      return questionCard;
    }

    return (
      <EditableQuestionCardShell
        canEdit={canEditQuestions}
        disabledReason={
          isPageMultiSelectActive || isSelectionActive
            ? 'Exit multi-select to reorder'
            : activeEdit.isAnyUnitActive
              ? 'Save or cancel current edit first'
              : structuralEditsRestricted
                ? restrictedReorderReason
                : undefined
        }
        isActive={activeEdit.activeEditUnitId === targetId}
        isDragDisabled={
          activeEdit.isAnyUnitActive ||
          structuralEditsRestricted ||
          isSelectionActive ||
          isPageMultiSelectActive
        }
        isSelected={selectedQuestionIdsSet.has(editableQuestionId)}
        isSelectionActive={isSelectionActive}
        isStructuralActionDisabled={structuralEditsRestricted}
        kind={section.kind}
        onCopyQuestion={handleQuestionCopy}
        onDeleteQuestion={handleQuestionDelete}
        onDuplicateQuestion={handleQuestionDuplicate}
        onEditQuestion={handleQuestionEdit}
        onSelectMultipleQuestion={handleQuestionSelectMultiple}
        onToggleSelectedQuestion={handleQuestionToggleSelected}
        questionId={editableQuestionId}
        questionLabel={question.label}
        restrictedDeleteReason={restrictedDeleteReason}
        restrictedDuplicateReason={restrictedDuplicateReason}
        targetId={targetId}
      >
        {questionCard}
      </EditableQuestionCardShell>
    );
  }

  function renderQuestionList(
    questions: NormalizedQuestionData[],
    options?: {
      headingLevel?: 4;
      isAssigned?: boolean;
      keyPrefix?: string;
    },
  ) {
    const sortableIds = questions.map((question) =>
      getQuestionEditTargetId(section.kind, getEditableQuestionId(question)),
    );
    const listContent = (
      <>
        {questions.map((question) => (
          <Fragment key={question.id}>
            {renderQuestionInsertDivider(
              getInsertIndexBefore(question),
              `${options?.keyPrefix ?? 'question'}-insert-before-${question.id}`,
            )}
            <li className={styles.listItem}>
              {renderQuestionItem({
                headingLevel: options?.headingLevel,
                isAssigned: options?.isAssigned,
                question,
              })}
            </li>
          </Fragment>
        ))}
      </>
    );

    if (!canEditQuestions) {
      return <ul className={styles.list}>{listContent}</ul>;
    }

    return (
      <DndContext
        collisionDetection={closestCenter}
        onDragCancel={handleQuestionDragCancel}
        onDragEnd={handleQuestionDragEnd}
        onDragStart={({ active }) => {
          setActiveDraggedQuestionId(String(active.id));
          setActiveDraggedQuestionWidth(active.rect.current.initial?.width ?? null);
        }}
        sensors={sortableSensors}
      >
        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
          <ul className={styles.list}>{listContent}</ul>
        </SortableContext>
        <DragOverlay adjustScale={false}>
          {activeDraggedQuestion ? (
            <QuestionDragPreview
              question={activeDraggedQuestion}
              width={activeDraggedQuestionWidth}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
    );
  }

  return (
    <section
      id={anchorId}
      className={[styles.section, className].filter(Boolean).join(' ')}
      data-page-panel={className ? 'true' : 'false'}
      ref={sectionRef}
    >
      <div className={styles.header}>
        <div>
          {/* Focusable from script only: where focus goes when the close
              takes away the control that had it. */}
          <h2 className={styles.heading} ref={headingRef} tabIndex={-1}>
            {isEmptyInEditMode ? 'Questions' : section.heading}
          </h2>
          <p className={styles.summary}>
            {isEmptyInEditMode ? 'No questions yet' : getScoreSummary(displaySection)}
          </p>
          {dueStatusText && !isEmptyInEditMode ? (
            <p className={styles.meta}>{dueStatusText}</p>
          ) : null}
        </div>
      </div>

      <p aria-atomic="true" aria-live="polite" className={styles.liveRegion}>
        {announcement}
      </p>

      {canEditQuestions &&
      structuralEditsRestricted ? (
        <div className={styles.restrictionBanner} role="status">
          {isEmptyInEditMode
            ? "Students can see this chapter, so questions can't be added."
            : restrictionMessage}
        </div>
      ) : null}

      {canEditQuestions &&
      isQuestionSelectionActive ? (
        <QuestionSelectionToolbar
          deleteDisabled={structuralEditsRestricted}
          deleteDisabledReason={restrictedDeleteReason}
          disabled={activeEdit?.isAnyUnitActive}
          onCancel={cancelSelection}
          onCopy={() => {
            if (!canRunBulkAction()) {
              return;
            }

            const selectedQuestions = getSelectedEditableQuestions();

            if (selectedQuestions.length > 0) {
              onCopyQuestions?.(section.kind, selectedQuestions);
            }
          }}
          onDelete={() => {
            if (structuralEditsRestricted || !canRunBulkAction()) {
              return;
            }

            const selectedQuestions = getSelectedEditableQuestions();

            if (
              selectedQuestions.length > 0 &&
              window.confirm(
                `Delete ${selectedQuestions.length} items? You can undo by canceling page edits before saving.`,
              )
            ) {
              activeEdit?.deleteQuestions(
                section.kind,
                selectedQuestions.map((question) => question.id),
              );
              cancelSelection();
            }
          }}
          scope={section.kind}
          selectedCount={selectedCount}
        />
      ) : null}

      {assignedQuestions.length > 0 ? (
        renderQuestionList(assignedQuestions, {
          isAssigned: true,
          keyPrefix: 'assigned',
        })
      ) : null}

      {practiceQuestions.length > 0 ? (
        <div className={styles.practiceSection}>
          <h3 className={styles.practiceHeading}>Practice Questions</h3>
          {renderQuestionList(practiceQuestions, {
            headingLevel: 4,
            isAssigned: false,
            keyPrefix: 'practice',
          })}
        </div>
      ) : null}

      {removedQuestionCount > 0 && userRole === 'instructor' ? (
        <p className={styles.removedNote}>
          {removedQuestionCount === 1
            ? '1 question was removed from this assignment.'
            : `${removedQuestionCount} questions were removed from this assignment.`}{' '}
          <Link
            className={styles.removedNoteLink}
            to={`/app/courses/${courseId}/grades/assignments/${componentId}`}
          >
            Manage in gradebook
          </Link>
        </p>
      ) : null}

      {renderAppendQuestionButton()}

      {transitionNotice ? (
        <p className={styles.transitionNotice}>{transitionNotice}</p>
      ) : null}
    </section>
  );
}

const EditableQuestionCardShell = memo(function EditableQuestionCardShell({
  canEdit,
  children,
  disabledReason,
  isActive,
  isDragDisabled,
  isSelected,
  isSelectionActive,
  isStructuralActionDisabled,
  kind,
  onCopyQuestion,
  onDeleteQuestion,
  onDuplicateQuestion,
  onEditQuestion,
  onSelectMultipleQuestion,
  onToggleSelectedQuestion,
  questionId,
  questionLabel,
  restrictedDeleteReason,
  restrictedDuplicateReason,
  targetId,
}: {
  canEdit: boolean;
  children: ReactNode;
  disabledReason?: string;
  isActive: boolean;
  isDragDisabled: boolean;
  isSelected: boolean;
  isSelectionActive: boolean;
  isStructuralActionDisabled: boolean;
  kind: QuestionSectionKind;
  onCopyQuestion: (kind: QuestionSectionKind, questionId: string) => void;
  onDeleteQuestion: (kind: QuestionSectionKind, questionId: string) => void;
  onDuplicateQuestion: (kind: QuestionSectionKind, questionId: string) => void;
  onEditQuestion: (kind: QuestionSectionKind, questionId: string) => void;
  onSelectMultipleQuestion: (
    kind: QuestionSectionKind,
    questionId: string,
  ) => void;
  onToggleSelectedQuestion: (
    kind: QuestionSectionKind,
    questionId: string,
  ) => void;
  questionId: string;
  // What the student sees the question called ("Question 1"): the controls'
  // names were built from questionId, an internal id (issue #92).
  questionLabel: string;
  restrictedDeleteReason?: string;
  restrictedDuplicateReason?: string;
  targetId: string;
}) {
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    disabled: !canEdit || isDragDisabled || isActive,
    id: targetId,
  });
  const dragTooltip = isDragDisabled
    ? isSelectionActive
      ? 'Exit multi-select to reorder'
      : disabledReason ?? 'Save or cancel current edit first'
    : 'Reorder';
  const isMenuDisabled =
    isSelectionActive ||
    disabledReason === 'Exit multi-select to reorder';
  const sortableStyle: CSSProperties = {
    transform: isDragging ? undefined : CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
  };
  const handleCopy = useCallback(() => {
    onCopyQuestion(kind, questionId);
  }, [kind, onCopyQuestion, questionId]);
  const handleDelete = useCallback(() => {
    onDeleteQuestion(kind, questionId);
  }, [kind, onDeleteQuestion, questionId]);
  const handleDuplicate = useCallback(() => {
    onDuplicateQuestion(kind, questionId);
  }, [kind, onDuplicateQuestion, questionId]);
  const handleEdit = useCallback(() => {
    onEditQuestion(kind, questionId);
  }, [kind, onEditQuestion, questionId]);
  const handleSelectMultiple = useCallback(() => {
    onSelectMultipleQuestion(kind, questionId);
  }, [kind, onSelectMultipleQuestion, questionId]);
  const handleToggleSelected = useCallback(() => {
    onToggleSelectedQuestion(kind, questionId);
  }, [kind, onToggleSelectedQuestion, questionId]);

  return (
    <div
      className={styles.questionShell}
      data-active-edit={isActive ? 'true' : 'false'}
      data-dragging={isDragging ? 'true' : 'false'}
      data-edit-mode={canEdit ? 'true' : 'false'}
      data-question-kind={kind}
      data-question-target-id={targetId}
      ref={setNodeRef}
      style={sortableStyle}
    >
      {canEdit && !isActive ? (
        <>
          <div className={[styles.cornerAffordance, styles.topLeft].join(' ')}>
            {isSelectionActive ? (
              <label className={styles.selectionCheckboxLabel}>
                <input
                  checked={isSelected}
                  className={styles.selectionCheckbox}
                  onChange={handleToggleSelected}
                  type="checkbox"
                />
                <span className={styles.liveRegion}>
                  Select {questionLabel}
                </span>
              </label>
            ) : (
              <Tooltip content={dragTooltip}>
                <button
                  {...attributes}
                  {...listeners}
                  aria-label="Reorder"
                  className={styles.affordanceButton}
                  disabled={isDragDisabled}
                  ref={setActivatorNodeRef}
                  title={dragTooltip}
                  type="button"
                >
                  <GripVertical aria-hidden="true" size={16} strokeWidth={2} />
                </button>
              </Tooltip>
            )}
          </div>
          <div className={[styles.cornerAffordance, styles.topRight].join(' ')}>
            <Tooltip content="Edit">
              <button
                aria-label="Edit"
                className={styles.affordanceButton}
                data-question-edit-button="true"
                onClick={handleEdit}
                title="Edit"
                type="button"
              >
                <Pencil aria-hidden="true" size={15} strokeWidth={2} />
              </button>
            </Tooltip>
            <QuestionCardMenu
              deleteDisabled={isStructuralActionDisabled}
              deleteDisabledReason={restrictedDeleteReason}
              disabled={isMenuDisabled}
              disabledReason={disabledReason}
              duplicateDisabled={isStructuralActionDisabled}
              duplicateDisabledReason={restrictedDuplicateReason}
              onCopy={handleCopy}
              onDelete={handleDelete}
              onDuplicate={handleDuplicate}
              onSelectMultiple={handleSelectMultiple}
              questionLabel={questionLabel}
            />
          </div>
        </>
      ) : null}
      {children}
    </div>
  );
});

function QuestionCardMenu({
  deleteDisabled,
  deleteDisabledReason,
  disabled,
  disabledReason,
  duplicateDisabled,
  duplicateDisabledReason,
  onCopy,
  onDelete,
  onDuplicate,
  onSelectMultiple,
  questionLabel,
}: {
  deleteDisabled: boolean;
  deleteDisabledReason?: string;
  disabled: boolean;
  disabledReason?: string;
  duplicateDisabled: boolean;
  duplicateDisabledReason?: string;
  onCopy: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onSelectMultiple: () => void;
  questionLabel: string;
}) {
  const tooltip = disabled ? disabledReason ?? 'Save or cancel current edit first' : 'More options';
  // Closing the menu hands focus back to its button, and the tooltip opens on
  // focus: Escape put "More options" over the button the menu had just left
  // (issue #92). That one focus is kept from the tooltip; focus by Tab, and
  // hover, still open it.
  const isFocusReturningRef = useRef(false);

  return (
    <DropdownMenu.Root modal={false}>
      <Tooltip content={tooltip}>
        <span>
          <DropdownMenu.Trigger asChild>
            <button
              aria-label={`${questionLabel} options`}
              className={styles.affordanceButton}
              disabled={disabled}
              onFocus={(event) => {
                if (isFocusReturningRef.current) {
                  isFocusReturningRef.current = false;
                  event.stopPropagation();
                }
              }}
              title={tooltip}
              type="button"
            >
              <MoreHorizontal aria-hidden="true" size={18} strokeWidth={2} />
            </button>
          </DropdownMenu.Trigger>
        </span>
      </Tooltip>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          className={styles.menuContent}
          onCloseAutoFocus={() => {
            // The menu focuses its button right after this. When it does not
            // (a click outside), the mark must not catch a later focus.
            isFocusReturningRef.current = true;
            window.setTimeout(() => {
              isFocusReturningRef.current = false;
            }, 0);
          }}
          sideOffset={8}
        >
          <DropdownMenu.Item
            className={styles.menuItem}
            onSelect={onCopy}
          >
            Copy
          </DropdownMenu.Item>
          <DropdownMenu.Item
            className={styles.menuItem}
            onSelect={onSelectMultiple}
          >
            Select Multiple
          </DropdownMenu.Item>
          <DropdownMenu.Separator className={styles.separator} />
          <DropdownMenu.Item
            className={styles.menuItem}
            disabled={duplicateDisabled}
            onSelect={onDuplicate}
            title={duplicateDisabled ? duplicateDisabledReason : undefined}
          >
            Duplicate
          </DropdownMenu.Item>
          <DropdownMenu.Separator className={styles.separator} />
          <DropdownMenu.Item
            className={[styles.menuItem, styles.dangerMenuItem].join(' ')}
            disabled={deleteDisabled}
            onSelect={onDelete}
            title={deleteDisabled ? deleteDisabledReason : undefined}
          >
            Delete
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function QuestionDragPreview({
  question,
  width,
}: {
  question: NormalizedQuestionData;
  width: number | null;
}) {
  const promptText = question.promptUnits
    .filter((unit) => unit.kind === 'text')
    .map((unit) => unit.html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={styles.dragPreview}
      style={width ? { width } : undefined}
    >
      <span className={styles.dragPreviewLabel}>{question.label}</span>
      <span className={styles.dragPreviewText}>
        {promptText || 'Question'}
      </span>
    </div>
  );
}

function QuestionInsertDivider({
  onInsert,
}: {
  onInsert: () => void;
}) {
  return (
    <li aria-hidden="false" className={styles.insertDividerItem}>
      <div className={styles.insertDivider}>
        <Tooltip content="Insert Question">
          <button
            aria-label="Insert Question"
            className={styles.insertButton}
            onClick={onInsert}
            title="Insert Question"
            type="button"
          >
            <Plus aria-hidden="true" size={14} strokeWidth={2.4} />
          </button>
        </Tooltip>
      </div>
    </li>
  );
}
